import type { LiveHelpRequestAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'

import {
  type AssociationDefinitions,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import PostQ from './PostQ'
import SessionQ from './SessionQ'

const LiveHelpRequestQ = {
  ...makeQueryModelV2<LiveHelpRequestAttributes>('live_help_request')([
    'id',
    'post_id',
    'session_id',
    'user_id',
    'canceled_at',
    'cancellation_reason',
    'completed_at',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    Post: {
      query: PostQ,
      foreignKey: PostQ.id,
      sourceKey: LiveHelpRequestQ.post_id,
    },
    Session: {
      query: SessionQ,
      foreignKey: SessionQ.id,
      sourceKey: LiveHelpRequestQ.session_id,
    },
  }),

  where: (
    {
      id,
      postId,
      sessionId,
      include = [],
      order = null,
      limit = null,
      paranoid = true,
    }: {
      id?: number | null | SQLFragment<number>
      postId?: number | null | SQLFragment<number>
      sessionId?: number | null | SQLFragment<number>
      include?: IncludeAssociations
      order?: QueryOrder | null
      limit?: number | null
      paranoid?: boolean
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<LiveHelpRequestAttributes> => SQL`
        FROM ${LiveHelpRequestQ}
        ${includeAssociationsToSql(LiveHelpRequestQ, include)}
        WHERE
            ${id != null ? SQL`${LiveHelpRequestQ.id} = ${id} AND` : SQL``}
            ${postId != null ? SQL`${LiveHelpRequestQ.post_id} = ${postId} AND` : SQL``}
            ${sessionId != null ? SQL`${LiveHelpRequestQ.session_id} = ${sessionId} AND` : SQL``}
            ${paranoid ? SQL`${LiveHelpRequestQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryOrderToSQL(order)}
        ${queryLimitToSQL(limit)}
    `,

  /**
   * Note: this query logic should match the logic in isLiveHelpRequestActive helper function
   */
  getActiveLiveHelpRequests: (userId: number): SQLFragment<LiveHelpRequestAttributes> =>
    LiveHelpRequestQ.where(
      {
        include: [
          { association: 'Post', required: true, where: SQL`${PostQ.user_id} = ${userId}` },
        ],
      },
      SQL`${LiveHelpRequestQ.canceled_at} IS NULL AND ${LiveHelpRequestQ.completed_at} IS NULL`
    ),

  countActiveLiveHelpRequests: (userId: number): SQLFragment<number> => SQL`
    COUNT(*)
      ${LiveHelpRequestQ.getActiveLiveHelpRequests(userId)}
  `,
}

export default LiveHelpRequestQ
